import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookText, CheckCheck, Trash2, Undo2 } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiClient, humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

interface TaxonomySummary {
  categoryKey: string;
  publishedVersion: number;
  publishedAt: string;
  publishedBy: string;
  draftUpdatedAt: string | null;
  draftUpdatedBy: string | null;
  draftSummary: string | null;
  isActive: boolean;
}

interface TaxonomyDetail extends TaxonomySummary {
  published: unknown;
  draft: unknown;
}

interface TaxonomyVersion {
  categoryKey: string;
  version: number;
  publishedAt: string;
  publishedBy: string;
  summary: string;
}

const keys = {
  all: ['taxonomy'] as const,
  list: () => [...keys.all, 'list'] as const,
  detail: (k: string) => [...keys.all, 'detail', k] as const,
  versions: (k: string) => [...keys.all, 'versions', k] as const,
  settings: () => [...keys.all, 'settings'] as const,
};

const api = {
  list: () => apiClient.get<{ taxonomies: TaxonomySummary[] }>('/taxonomy'),
  detail: (k: string) =>
    apiClient.get<{ taxonomy: TaxonomyDetail }>(`/taxonomy/${encodeURIComponent(k)}`),
  versions: (k: string) =>
    apiClient.get<{ versions: TaxonomyVersion[] }>(
      `/taxonomy/${encodeURIComponent(k)}/versions`,
    ),
  discardDraft: (k: string) =>
    apiClient.delete<{ success: boolean }>(`/taxonomy/${encodeURIComponent(k)}/draft`),
  publishDraft: (k: string, summary: string) =>
    apiClient.post<{ taxonomy: TaxonomyDetail }>(
      `/taxonomy/${encodeURIComponent(k)}/publish`,
      { summary },
    ),
  rollback: (k: string, targetVersion: number, reason: string) =>
    apiClient.post<{ taxonomy: TaxonomyDetail }>(
      `/taxonomy/${encodeURIComponent(k)}/rollback`,
      { targetVersion, reason },
    ),
};

export function TaxonomyListPage() {
  const q = useQuery({ queryKey: keys.list(), queryFn: () => api.list(), staleTime: 60_000 });

  return (
    <div className="space-y-4">
      <PageHeader
        title="AI taxonomy"
        description="Per-category approach / ladder / profiling taxonomies used by the goal-intake pipeline (ADR-019)."
      />

      {q.isLoading && <LoadingState />}
      {q.isError && (
        <ErrorState
          title="Taxonomies failed"
          description={humanizeError(q.error)}
          actionLabel="Retry"
          onAction={() => q.refetch()}
        />
      )}
      {q.data && (
        <Card>
          <CardContent className="p-0">
            {q.data.taxonomies.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                No taxonomies configured.
              </p>
            ) : (
              <ul className="divide-y">
                {q.data.taxonomies.map((t) => (
                  <li key={t.categoryKey} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <Link
                        to={`/taxonomy/${encodeURIComponent(t.categoryKey)}`}
                        className="font-medium hover:underline"
                      >
                        <BookText className="mr-1 inline h-3.5 w-3.5" />
                        {t.categoryKey}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        v{t.publishedVersion} · published{' '}
                        {t.publishedAt.slice(0, 10)} by {t.publishedBy}
                        {t.draftUpdatedAt && ` · draft by ${t.draftUpdatedBy ?? 'unknown'}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.draftUpdatedAt && <Badge variant="warning">Has draft</Badge>}
                      {t.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function TaxonomyDetailPage() {
  const { categoryKey = '' } = useParams<{ categoryKey: string }>();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: keys.detail(categoryKey),
    queryFn: () => api.detail(categoryKey),
    enabled: !!categoryKey,
  });
  const versions = useQuery({
    queryKey: keys.versions(categoryKey),
    queryFn: () => api.versions(categoryKey),
    enabled: !!categoryKey,
  });

  const [publishDialog, setPublishDialog] = React.useState(false);
  const [discardDialog, setDiscardDialog] = React.useState(false);
  const [rollbackTarget, setRollbackTarget] = React.useState<TaxonomyVersion | null>(null);

  const discard = useMutation({
    mutationFn: () => api.discardDraft(categoryKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      toast({ title: 'Draft discarded', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Discard failed', description: humanizeError(err), variant: 'destructive' }),
  });

  const publish = useMutation({
    mutationFn: (summary: string) => api.publishDraft(categoryKey, summary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      toast({ title: 'Draft published', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Publish failed', description: humanizeError(err), variant: 'destructive' }),
  });

  const rollback = useMutation({
    mutationFn: ({ target, reason }: { target: number; reason: string }) =>
      api.rollback(categoryKey, target, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      toast({ title: 'Rolled back', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Rollback failed', description: humanizeError(err), variant: 'destructive' }),
  });

  if (detail.isLoading) return <LoadingState />;
  if (detail.isError || !detail.data)
    return (
      <ErrorState
        title="Taxonomy failed to load"
        description={detail.error ? humanizeError(detail.error) : 'Not found.'}
      />
    );

  const t = detail.data.taxonomy;

  return (
    <div className="space-y-4">
      <Link
        to="/taxonomy"
        className="inline-flex text-sm text-muted-foreground hover:text-foreground"
      >
        ← Back to taxonomies
      </Link>
      <PageHeader
        title={t.categoryKey}
        description={`v${t.publishedVersion} · published ${t.publishedAt.slice(0, 10)} by ${t.publishedBy}`}
        actions={
          <div className="flex items-center gap-2">
            {t.draftUpdatedAt && (
              <>
                <Button size="sm" onClick={() => setPublishDialog(true)}>
                  <CheckCheck className="mr-1 h-4 w-4" /> Publish draft
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDiscardDialog(true)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Discard
                </Button>
              </>
            )}
          </div>
        }
      />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Published</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-[500px] overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
              {JSON.stringify(t.published, null, 2)}
            </pre>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Draft {t.draftSummary && <span className="text-xs text-muted-foreground">({t.draftSummary})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {t.draft ? (
              <pre className="max-h-[500px] overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
                {JSON.stringify(t.draft, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">No draft in progress.</p>
            )}
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader><CardTitle className="text-base">Version history</CardTitle></CardHeader>
        <CardContent className="p-0">
          {versions.isLoading ? (
            <LoadingState />
          ) : versions.isError ? (
            <ErrorState title="Versions failed" description={humanizeError(versions.error)} />
          ) : (
            <ul className="divide-y text-sm">
              {(versions.data?.versions ?? []).map((v) => (
                <li key={v.version} className="flex items-center justify-between px-4 py-2">
                  <div>
                    <span className="font-medium">v{v.version}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {v.publishedAt.slice(0, 10)} by {v.publishedBy}
                    </span>
                    {v.summary && <p className="text-xs text-muted-foreground">{v.summary}</p>}
                  </div>
                  {v.version !== t.publishedVersion && (
                    <Button size="sm" variant="outline" onClick={() => setRollbackTarget(v)}>
                      <Undo2 className="mr-1 h-3.5 w-3.5" /> Rollback
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {publishDialog && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPublishDialog(false)}
          title={`Publish draft for "${t.categoryKey}"?`}
          description="The draft replaces the current published version. A new version number is assigned."
          reasonPlaceholder="Short summary for the version history"
          submitting={publish.isPending}
          onConfirm={async ({ reason }) => {
            await publish.mutateAsync(reason);
            setPublishDialog(false);
          }}
        />
      )}
      {discardDialog && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setDiscardDialog(false)}
          title="Discard draft?"
          description="The current draft is permanently removed. The published version is unchanged."
          destructive
          reasonRequired={false}
          confirmLabel="Discard"
          submitting={discard.isPending}
          onConfirm={async () => {
            await discard.mutateAsync();
            setDiscardDialog(false);
          }}
        />
      )}
      {rollbackTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setRollbackTarget(null)}
          title={`Rollback to v${rollbackTarget.version}?`}
          description="Publishes the snapshot of the target version as a new current version."
          destructive
          confirmLabel="Rollback"
          submitting={rollback.isPending}
          onConfirm={async ({ reason }) => {
            await rollback.mutateAsync({ target: rollbackTarget.version, reason });
            setRollbackTarget(null);
          }}
        />
      )}
    </div>
  );
}
